import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const scrollToDemo = () => {
  window.location.href = '/?scrollTo=demo';
};

const faqs = [
  {
    q: 'What is an AI Chief of Staff for revenue?',
    a: 'An AI Chief of Staff for revenue is an autonomous system that sits on top of the CRM and does the operational work a human Chief of Staff would do for a CRO: keeping the pipeline clean, producing evidence-based forecasts, flagging deals at risk, and delivering a weekly digest of what actually changed. Unlike a copilot, it executes — it does not just suggest.',
  },
  {
    q: 'How is this different from a sales AI copilot?',
    a: 'A copilot waits for a rep to ask it something. An AI Chief of Staff runs autonomously: it listens to calls, reads emails, joins meetings, drafts approved CRM updates, scores every deal on real buyer signals, and writes the weekly forecast brief for the CRO. The rep approves; the CRO reads. No prompts, no dashboards to log into.',
  },
  {
    q: 'Which CRMs does NoQ5 work with?',
    a: 'NoQ5 is CRM-agnostic and runs on HubSpot, Salesforce and Pipedrive in production today, with Microsoft Dynamics and Zoho on the roadmap. One platform across the revenue stack, not a CRM-native add-on.',
  },
  {
    q: 'Who is this built for?',
    a: 'Chief Revenue Officers, VPs of Sales and RevOps leaders at B2B companies who need defensible forecasts, clean pipeline data and proactive deal-risk visibility — without adding headcount or building it in-house.',
  },
  {
    q: 'Is it GDPR-compliant?',
    a: 'Yes. NoQ5 is EU-hosted, AES-256-GCM encrypted, with full audit trail and ISO 27001 in progress. Data residency stays in the EU; sub-processors are listed publicly.',
  },
  {
    q: 'How much does it cost?',
    a: 'EUR 59 per user per month, billed annually. One tier, no quote-only enterprise upcharge for the same product.',
  },
  {
    q: 'How fast does it deploy?',
    a: 'Most teams are live within a week. OAuth into HubSpot, Salesforce or Pipedrive; connect Gmail, Outlook, Zoom or Meet; NoQ5 begins drafting CRM updates from the next conversation forward.',
  },
];

const AIChiefOfStaffForRevenue = () => {
  return (
    <Layout>
      <SEOHead
        title="AI Chief of Staff for Revenue: Autonomous CRM, Forecasts & Pipeline Visibility | NoQ5"
        description="The AI Chief of Staff for Chief Revenue Officers. Autonomous CRM updates, evidence-based forecasts, deal-risk alerts and a Monday morning digest. EU-hosted."
        keywords="AI chief of staff for revenue, AI chief of staff CRO, autonomous CRM, AI for revenue leaders, AI revenue operations, evidence-based forecasting, CRO AI platform, AI revenue intelligence, autonomous sales AI, AI chief of staff sales"
        canonicalUrl="https://noq5.ai/ai-chief-of-staff-for-revenue"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'AI Chief of Staff for Revenue',
            description:
              'NoQ5 is the AI Chief of Staff for revenue teams: autonomous CRM, evidence-based forecasts, proactive deal-risk alerts and a Monday morning digest.',
            url: 'https://noq5.ai/ai-chief-of-staff-for-revenue',
            publisher: { '@type': 'Organization', name: 'NoQ5.ai', url: 'https://noq5.ai' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://noq5.ai/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'AI Chief of Staff for Revenue',
                item: 'https://noq5.ai/ai-chief-of-staff-for-revenue',
              },
            ],
          },
        ]}
      />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/15 mb-6">
              <span className="text-sm font-medium text-foreground/80">Category Pillar</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              AI Chief of Staff for Revenue
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              An AI Chief of Staff for revenue is an autonomous system that runs the operational work behind a CRO: clean pipeline, evidence-based forecasts, proactive deal-risk alerts and a weekly digest of what actually changed. NoQ5 is built to be that system for B2B revenue teams on HubSpot, Salesforce and Pipedrive.
            </p>
            <div className="flex justify-center gap-3 mt-8">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-sm"
                onClick={scrollToDemo}
              >
                Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>

          {/* Definition / answer-first */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              What does an AI Chief of Staff for revenue actually do?
            </h2>
            <p className="text-lg text-foreground/90 mb-6">
              It executes the four jobs a CRO would otherwise hire a human Chief of Staff for, and runs them autonomously every day:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                {
                  title: 'Keeps the CRM truthful',
                  body: 'Captures every call, email and meeting, drafts the CRM update, the rep approves in one tap. 95%+ data completeness in weeks, not quarters.',
                },
                {
                  title: 'Produces evidence-based forecasts',
                  body: 'Scores every deal on real buyer engagement signals — not rep optimism. Every forecast number has a trail.',
                },
                {
                  title: 'Flags deals at risk before they slip',
                  body: 'Proactive alerts when a deal goes dark, a champion goes quiet or the buyer signal weakens. The CRO sees risk in hours, not at QBR.',
                },
                {
                  title: 'Delivers the Monday Morning Digest',
                  body: 'A written brief in the CRO inbox every Monday: pipeline movement, deals at risk, forecast changes with evidence, top wins, and the actions to take this week.',
                },
              ].map((j) => (
                <div key={j.title} className="p-6 bg-card border border-border rounded-sm">
                  <h3 className="font-semibold text-foreground mb-2">{j.title}</h3>
                  <p className="text-sm text-muted-foreground">{j.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Why now */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Why CROs need an AI Chief of Staff, not another copilot
            </h2>
            <p className="text-lg text-foreground/90 mb-4">
              The average CRO tenure is roughly 18 months, and the most common reason for replacement is a missed forecast built on bad CRM data. A copilot that helps a rep draft a follow-up email does not solve that. An autonomous system that keeps the pipeline clean and the forecast defensible does.
            </p>
            <p className="text-lg text-foreground/90">
              NoQ5 is the only platform purpose-built around the CRO's job, not the rep's productivity. The rep gets time back as a side effect; the CRO gets a forecast they can defend to the board.
            </p>
          </section>

          {/* Comparisons cluster */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-6">How NoQ5 compares</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { to: '/comparison/salesforce-einstein', label: 'NoQ5 vs Salesforce Einstein / Agentforce' },
                { to: '/comparison/hubspot', label: 'NoQ5 vs HubSpot Breeze' },
                { to: '/noq5-vs-agentforce', label: 'NoQ5 vs Agentforce' },
                { to: '/noq5-vs-hubspot-ai', label: 'NoQ5 vs HubSpot AI' },
                { to: '/noq5-vs-relevance-ai', label: 'NoQ5 vs Relevance AI' },
                { to: '/apollo-vs-noq5', label: 'NoQ5 vs Apollo' },
              ].map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  className="p-4 bg-card border border-border rounded-sm hover:border-primary/40 transition-colors flex items-center justify-between"
                >
                  <span className="font-medium text-foreground">{c.label}</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </section>

          {/* Related platform pages */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-6">Explore the platform</h2>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                { to: '/ai-for-cros', label: 'AI for CROs', desc: 'The persona page' },
                { to: '/product/command-center', label: 'Command Center', desc: 'The CRO war room' },
                { to: '/crm-agent', label: 'CRM Agent', desc: 'The autonomous CRM layer' },
                { to: '/sales-forecasting-software', label: 'Sales Forecasting', desc: 'Evidence-based' },
                { to: '/revenue-intelligence', label: 'Revenue Intelligence', desc: 'Live pipeline truth' },
                { to: '/deal-intelligence', label: 'Deal Intelligence', desc: 'Per-deal risk scoring' },
              ].map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  className="p-4 bg-card border border-border rounded-sm hover:border-primary/40 transition-colors"
                >
                  <h3 className="font-semibold text-foreground mb-1">{c.label}</h3>
                  <p className="text-sm text-muted-foreground">{c.desc}</p>
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-6">Frequently asked questions</h2>
            <div className="space-y-4">
              {faqs.map((f) => (
                <div key={f.q} className="p-6 bg-card border border-border rounded-sm">
                  <h3 className="font-semibold text-foreground mb-2 flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    {f.q}
                  </h3>
                  <p className="text-foreground/90 pl-7">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="text-center p-12 bg-primary/5 border border-primary/20 rounded-sm">
            <h2 className="text-3xl font-bold text-foreground mb-4">See your AI Chief of Staff in action</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              30-minute working demo on your real CRM. EU-hosted, GDPR-compliant.
            </p>
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-sm"
              onClick={scrollToDemo}
            >
              Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AIChiefOfStaffForRevenue;
